import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Box,
  Button,
  Radio,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  InputLabel,
  CircularProgress,
  ListSubheader,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Colors from "../../../assets/Variables/ColorsVariables.js";
import * as TextVariables from "../../../assets/Variables/TextVariables.js";

import NotificationModal from "../../../components/Notifications/NotificationModal.js";
import SteeperComponent from "../../../components/Steeper/SteeperComponent.js";

const steps = ["IB / Agent", "Sub Accounts", "Configurations"];

function CreateMAMAccount() {
  const navigate = useNavigate();

  const [activeStep] = useState(1);
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });

  const [submitting, setSubmitting] = useState(false);
  const [ibMode, setIbMode] = useState("select");
  const [ibs, setIbs] = useState([]);
  const [loadingIBs, setLoadingIBs] = useState(false);
  const [ibSearch, setIbSearch] = useState("");

  const [formValues, setFormValues] = useState({
    ib: "",
    accountEmail: "",
    platform: "",
    currency: "",
    leverage: "",
    deposit: "",
    comment: "",
  });

  const handleCloseNotification = () => setShowNotification(false);

  const showWarn = (title, message) => {
    setModalProps({
      iconType: "warning",
      title,
      message,
      onAcceptLabel: "Close",
      onRefuseLabel: undefined,
      onAccept: () => setShowNotification(false),
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  const showError = (title, message) => {
    setModalProps({
      iconType: "error",
      title,
      message,
      onAcceptLabel: "Close",
      onRefuseLabel: undefined,
      onAccept: () => setShowNotification(false),
      onRefuse: undefined,
    });
    setShowNotification(true);
  };

  const handleClickToBackPage = () => navigate("/requests");

  const normalizeIbs = (rawList) => {
    if (!Array.isArray(rawList)) return [];

    return rawList
      .map((x) => ({
        id: String(x?.id ?? ""),
        name: x?.name ?? "Unnamed",
        email: x?.email_ib ?? "",
      }))
      .filter((x) => x.id && x.email);
  };

  const filteredIbs = useMemo(() => {
    const q = ibSearch.trim().toLowerCase();
    if (!q) return ibs;

    return ibs.filter((x) => {
      const name = (x.name || "").toLowerCase();
      const email = (x.email || "").toLowerCase();
      const id = String(x.id || "").toLowerCase();
      return name.includes(q) || email.includes(q) || id.includes(q);
    });
  }, [ibs, ibSearch]);

  useEffect(() => {
    const loadIBsFromLocalStorage = () => {
      setLoadingIBs(true);

      try {
        const raw = localStorage.getItem(process.env.REACT_APP_WEB_PREFIX + "ibs");
        const parsedRaw = raw ? JSON.parse(raw) : [];
        const parsed = normalizeIbs(parsedRaw);

        setIbs(parsed);

        if (!parsed.length) {
          setFormValues((p) => ({
            ...p,
            ib: "",
            accountEmail: "",
          }));
        }
      } catch (error) {
        console.error("Error loading IBs from localStorage:", error);
        setIbs([]);
        setFormValues((p) => ({
          ...p,
          ib: "",
          accountEmail: "",
        }));
        showError("Error loading IB list", "We couldn't load the IB list from local storage.");
      } finally {
        setLoadingIBs(false);
      }
    };

    if (ibMode === "select") {
      setFormValues((p) => ({
        ...p,
        ib: "",
        accountEmail: "",
        platform: "",
      }));
      setIbSearch("");
      loadIBsFromLocalStorage();
    } else {
      setFormValues((p) => ({
        ...p,
        ib: "",
        accountEmail: "",
        platform: "",
      }));
      setIbSearch("");
    }
  }, [ibMode]);

  const errorsStep1 = useMemo(() => {
    const e = {
      ib: false,
      accountEmail: false,
    };

    if (ibMode === "select") {
      if (!formValues.ib) e.ib = true;
    } else {
      const hasIb = formValues.ib.trim();
      const hasEmail = formValues.accountEmail.trim();

      if (!hasIb && !hasEmail) {
        e.ib = true;
        e.accountEmail = true;
      }
    }

    return e;
  }, [formValues, ibMode]);

  const canContinueStep1 =
    !errorsStep1.ib &&
    !errorsStep1.accountEmail &&
    !submitting &&
    !(ibMode === "select" && loadingIBs);

  const handleSelectIb = (ibId) => {
    const selected = ibs.find((x) => x.id === ibId);

    setFormValues((p) => ({
      ...p,
      ib: ibId,
      accountEmail: selected?.email || "",
    }));
  };

  const handleContinue = () => {
    setSubmittedOnce(true);

    if (!canContinueStep1) {
      showWarn("Incomplete fields", "Please fill in the missing fields.");
      return;
    }

    navigate("/requests/create-mam-account/step-two", {
      state: {
        ibMode,
        ibId: formValues.ib,
        accountEmail: formValues.accountEmail,
        ibPlatform: formValues.platform,
      },
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 4,
        maxWidth: "calc(100vw - 280px)",
        minHeight: "calc(100vh - 100px)",
        marginTop: "0px",
        marginLeft: "10px",
        borderRadius: "12px",
        backgroundColor: "#F9F9F9",
      }}
    >
      <NotificationModal
        open={showNotification}
        onClose={handleCloseNotification}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <Button
          onClick={handleClickToBackPage}
          startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
          sx={{ textTransform: "none", color: "black", fontWeight: "bold" }}
        >
          Go back
        </Button>
      </Box>

      <Box sx={{ width: "70%", margin: "0 auto", marginTop: "6px" }}>
        <SteeperComponent stepList={steps} stepNumber={activeStep} />
      </Box>

      <Paper
        elevation={2}
        sx={{
          padding: 4,
          maxWidth: "70%",
          minHeight: "260px",
          margin: "18px auto 0 auto",
          borderRadius: "12px",
        }}
      >
        <Typography sx={{ fontFamily: TextVariables.Body_3_Bold, marginBottom: "16px" }}>
          Step 1: IB / Agent
        </Typography>

        <Typography
          sx={{
            fontFamily: TextVariables.Caption_1,
            color:
              submittedOnce && (errorsStep1.ib || errorsStep1.accountEmail)
                ? "#d32f2f"
                : "transparent",
            marginTop: "4px",
          }}
        >
          {submittedOnce && (errorsStep1.ib || errorsStep1.accountEmail)
            ? "Please enter IB / Agent or Email"
            : "."}
        </Typography>

        <Typography
          sx={{
            fontFamily: TextVariables.Caption_2,
            color: "#6B7280",
            marginBottom: "8px",
          }}
        >
          Type of account you are requesting
        </Typography>

        <Box sx={{ display: "flex", gap: 2, marginBottom: 3 }}>
          <Box
            onClick={() => setIbMode("select")}
            sx={{
              flex: 1,
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "10px 14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              backgroundColor: "#FFF",
            }}
          >
            <FormControlLabel
              value="select"
              control={<Radio checked={ibMode === "select"} />}
              label="Select from IB List"
              sx={{ margin: 0 }}
            />
          </Box>

          <Box
            onClick={() => setIbMode("manual")}
            sx={{
              flex: 1,
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              padding: "10px 14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              backgroundColor: "#FFF",
            }}
          >
            <FormControlLabel
              value="manual"
              control={<Radio checked={ibMode === "manual"} />}
              label="Fill manually"
              sx={{ margin: 0 }}
            />
          </Box>
        </Box>

        {ibMode === "select" ? (
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <FormControl fullWidth error={submittedOnce && errorsStep1.ib}>
              <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
                IB
              </Typography>

              <Select
                value={formValues.ib}
                label="IB"
                onChange={(e) => handleSelectIb(e.target.value)}
                disabled={ibMode !== "select" || loadingIBs}
                sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
                onClose={() => setIbSearch("")}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      borderRadius: "12px",
                      boxShadow: "0px 10px 25px rgba(0,0,0,0.12)",
                      overflow: "hidden",
                    },
                  },
                  MenuListProps: { sx: { py: 0 } },
                }}
                renderValue={(val) => {
                  if (!val) return <em>Select the IB</em>;
                  const selected = ibs.find((x) => x.id === val);
                  return selected ? selected.name : val;
                }}
              >
                <ListSubheader
                  sx={{ backgroundColor: "#FFF", py: 1.5, px: 1.5 }}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <TextField
                    fullWidth
                    autoFocus
                    size="small"
                    value={ibSearch}
                    onChange={(e) => setIbSearch(e.target.value)}
                    placeholder="Search"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#FFF",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>

                <MenuItem value="">
                  <em>Select the IB</em>
                </MenuItem>

                {loadingIBs && (
                  <MenuItem disabled>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontFamily: TextVariables.Caption_2 }}>
                        Loading IB list...
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

                {!loadingIBs &&
                  filteredIbs.map((ib) => (
                    <MenuItem
                      key={ib.id}
                      value={ib.id}
                      sx={{
                        "&.Mui-selected": { backgroundColor: "#EAF2FF !important" },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#DDEBFF !important",
                        },
                      }}
                    >
                      {ib.name}
                    </MenuItem>
                  ))}

                {!loadingIBs && filteredIbs.length === 0 && (
                  <MenuItem disabled>No results</MenuItem>
                )}
              </Select>

              <Typography
                sx={{
                  fontFamily: TextVariables.Caption_1,
                  color: submittedOnce && errorsStep1.ib ? "#d32f2f" : "transparent",
                  marginTop: "4px",
                }}
              >
                {submittedOnce && errorsStep1.ib ? "Please, select an IB" : "."}
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <Typography sx={{ fontFamily: TextVariables.Caption_2, mb: 1, color: "#374151" }}>
                Account Email
              </Typography>

              <OutlinedInput
                value={formValues.accountEmail}
                label="Account Email"
                disabled
                sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
                placeholder="Enter account email"
              />
              <Typography
                sx={{
                  fontFamily: TextVariables.Caption_1,
                  color: "transparent",
                  marginTop: "4px",
                }}
              >
                .
              </Typography>
            </FormControl>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <FormControl fullWidth error={submittedOnce && errorsStep1.ib}>
                <InputLabel>IB / Agent</InputLabel>
                <OutlinedInput
                  value={formValues.ib}
                  label="IB / Agent"
                  onChange={(e) =>
                    setFormValues((p) => ({
                      ...p,
                      ib: e.target.value.slice(0, 120),
                    }))
                  }
                  sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
                  placeholder="Enter IB or Agent number"
                />
                <Typography
                  sx={{
                    fontFamily: TextVariables.Caption_1,
                    color: submittedOnce && errorsStep1.ib ? "#d32f2f" : "transparent",
                    marginTop: "4px",
                  }}
                >
                  {submittedOnce && errorsStep1.ib ? "Please, enter an IB / Agent" : "."}
                </Typography>
              </FormControl>

              <FormControl fullWidth error={submittedOnce && errorsStep1.accountEmail}>
                <InputLabel>Email (Optional)</InputLabel>
                <OutlinedInput
                  value={formValues.accountEmail}
                  label="Email (Optional)"
                  onChange={(e) =>
                    setFormValues((p) => ({
                      ...p,
                      accountEmail: e.target.value.slice(0, 120),
                    }))
                  }
                  sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
                  placeholder="Enter IB or Agent email"
                />
                <Typography
                  sx={{
                    fontFamily: TextVariables.Caption_1,
                    color:
                      submittedOnce && errorsStep1.accountEmail ? "#d32f2f" : "transparent",
                    marginTop: "4px",
                  }}
                >
                  {submittedOnce && errorsStep1.accountEmail ? "Please, enter an email" : "."}
                </Typography>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", marginTop: 1 }}>
              <FormControl fullWidth sx={{ maxWidth: "calc(50% - 8px)" }}>
                <InputLabel shrink>Platform (Optional)</InputLabel>

                <Select
                  value={formValues.platform}
                  label="Platform (Optional)"
                  onChange={(e) =>
                    setFormValues((p) => ({
                      ...p,
                      platform: e.target.value,
                    }))
                  }
                  sx={{ backgroundColor: "#FFF", borderRadius: "10px" }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9CA3AF" }}>
                          Select the agent's platform
                        </span>
                      );
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="MT4 LTD LIVE 1">MT4 LTD LIVE 1</MenuItem>
                  <MenuItem value="MT4 LTD LIVE 2">MT4 LTD LIVE 2</MenuItem>
                  <MenuItem value="MT5 LTD LIVE 1">MT5 LTD LIVE 1</MenuItem>
                </Select>

                <Typography
                  sx={{
                    fontFamily: TextVariables.Caption_1,
                    color: "transparent",
                    marginTop: "4px",
                  }}
                >
                  .
                </Typography>
              </FormControl>
            </Box>
          </>
        )}

        <Box sx={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!canContinueStep1}
            sx={{
              marginTop: "12px",
              backgroundColor: canContinueStep1 ? Colors.Primary_600 : "#E5E7EB",
              color: canContinueStep1 ? "#FFF" : "#6B7280",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              paddingX: 4,
              paddingY: 1.2,
              "&:hover": { backgroundColor: Colors.Primary_700 },
              "&:disabled": { color: "#6B7280" },
            }}
          >
            Continue
          </Button>
        </Box>
      </Paper>

      <Box sx={{ marginBottom: "100px" }} />
    </Paper>
  );
}

export default CreateMAMAccount;